import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardBody,
  FormGroup,
  InputGroup,
  Input,
  Button,
  Collapse
} from 'reactstrap';
import continents from './continent_countries.json';
import Checkbox from '../Checkbox/Checkbox';

// Memoized continent component to prevent unnecessary re-renders
const ContinentCard = React.memo(
  ({
    continent,
    index,
    totalContinents,
    allCountriesCount,
    activeItems,
    onSelectAll,
    onContinentChange,
    onSelect,
    selected,
    activeLanguage,
    translate,
    prefix
  }) => {
    const { code, filtered, selectedCountries, totalCount, selectedState } =
      continent;

    // Return no countries message
    if (index === totalContinents - 1 && !allCountriesCount) {
      return (
        <Card className="mb-2" key="not_found">
          <CardBody className="collapse-header">
            {translate('country_not_found')}
          </CardBody>
        </Card>
      );
    }

    // Hide continent if there are no countries
    if (!totalCount) return null;

    return (
      <Card key={`${prefix}${code}`} className="mb-2">
        <CardBody className="collapse-header">
          <div className="title">
            <Checkbox
              name={code}
              id={`${prefix}${code}`}
              checked={!selectedState}
              onChange={() => onSelectAll(code, selectedState)}
              label={`${continent[activeLanguage]} (${selectedCountries}/${totalCount})`}
            />
          </div>
          <div className="actions">
            <Button color="orange" onClick={() => onContinentChange(code)}>
              <i
                className={
                  activeItems[code]
                    ? 'far fa-angle-double-up'
                    : 'far fa-angle-double-down'
                }
              />{' '}
              {translate('general_toggle')}
            </Button>
          </div>
        </CardBody>
        <Collapse isOpen={activeItems[code]}>
          <CardBody>
            <div className="countries-container">
              {activeItems[code] &&
                filtered.map(country => {
                  const isChecked = selected.includes(country.code);
                  const name = country.code;
                  const label = `${country[activeLanguage]} (+${country.dial_code})`;
                  const flag = country.code.toLowerCase();

                  return (
                    <FormGroup key={country.code} check className="checkbox">
                      <Checkbox
                        id={`${prefix}_${code}_${country.code}_checkbox`}
                        name={name}
                        onChange={onSelect}
                        checked={isChecked}
                        value={isChecked}
                        label={
                          <>
                            <i
                              style={{ width: '30px' }}
                              className={`flag-icon flag-icon-${flag} mr-1`}
                            />
                            {label}
                          </>
                        }
                      />
                    </FormGroup>
                  );
                })}
            </div>
          </CardBody>
        </Collapse>
      </Card>
    );
  }
);

const propTypes = {
  selected: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  translate: PropTypes.func.isRequired,
  activeLanguage: PropTypes.oneOf(['en', 'fr', 'nl']),
  prefix: PropTypes.string.isRequired
};

const defaultProps = {
  selected: [],
  activeLanguage: 'en'
};

const ContinentCountrySelect = ({
  selected = [],
  onChange,
  translate,
  activeLanguage = 'en',
  prefix
}) => {
  const [activeItems, setActiveItems] = useState({
    AF: false,
    AN: false,
    AS: false,
    OC: false,
    EU: false,
    NA: false,
    SA: false
  });
  const [query, setQuery] = useState('');

  const onContinentChange = useCallback(item => {
    setActiveItems(prev => ({
      ...prev,
      [item]: !prev[item]
    }));
  }, []);

  const onSearch = useCallback(event => {
    const value = event.target.value;
    const openMenu = value.trim().length >= 3;

    setActiveItems(prev => {
      const newActiveItems = { ...prev };
      Object.keys(newActiveItems).forEach(key => {
        newActiveItems[key] = openMenu;
      });
      return newActiveItems;
    });

    setQuery(value);
  }, []);

  const onClear = useCallback(() => {
    setActiveItems({
      AF: false,
      AN: false,
      AS: false,
      OC: false,
      EU: false,
      NA: false,
      SA: false
    });
    setQuery('');
  }, []);

  const onSelect = useCallback(
    event => {
      const target = event.target;
      const name = target.name;
      const value = target.type === 'checkbox' ? target.checked : target.value;

      // Create new array instead of mutating
      const newSelected = [...selected];

      if (value) {
        if (!newSelected.includes(name)) {
          newSelected.push(name);
        }
      } else {
        const index = newSelected.indexOf(name);
        if (index > -1) {
          newSelected.splice(index, 1);
        }
      }
      onChange(newSelected);
    },
    [selected, onChange]
  );

  const onSelectAll = useCallback(
    (item, selectedState) => {
      const continent = continents.find(c => c.code === item);
      const newSelected = [...selected];

      continent.countries.forEach(c => {
        if (selectedState) {
          if (!newSelected.includes(c.code)) {
            newSelected.push(c.code);
          }
        } else {
          const index = newSelected.indexOf(c.code);
          if (index > -1) {
            newSelected.splice(index, 1);
          }
        }
      });

      onChange(newSelected);
    },
    [selected, onChange]
  );

  // Memoized filtered continents for performance
  const filteredContinents = useMemo(() => {
    const userQuery = query.trim().toLowerCase();
    const shouldFilter = userQuery.length >= 3 && query.length <= 20;

    return continents.map(continent => {
      const filtered = shouldFilter
        ? continent.countries.filter(c => {
            const countryName = c[activeLanguage].toLowerCase();
            return countryName.indexOf(userQuery) !== -1;
          })
        : continent.countries;

      const filterSelected = filtered.filter(c => selected.includes(c.code));
      const selectedCountries = filterSelected.length;
      const totalCount = filtered.length;
      const selectedState = selectedCountries !== totalCount;

      return {
        ...continent,
        filtered,
        selectedCountries,
        totalCount,
        selectedState
      };
    });
  }, [query, activeLanguage, selected]);

  // Calculate total countries count
  const allCountriesCount = useMemo(() => {
    return filteredContinents.reduce(
      (total, continent) => total + continent.totalCount,
      0
    );
  }, [filteredContinents]);

  return (
    <div>
      <FormGroup>
        <InputGroup>
          <Input
            className="form-control"
            name="query"
            value={query}
            placeholder="Search..."
            onChange={onSearch}
          />
          {query && (
            <button
              type="button"
              className="btn"
              style={{ marginLeft: '-40px', zIndex: '100' }}
              onClick={onClear}
            >
              &times;
            </button>
          )}
        </InputGroup>
      </FormGroup>
      {filteredContinents.map((continent, index) => (
        <ContinentCard
          key={`${prefix}${continent.code}`}
          continent={continent}
          index={index}
          totalContinents={filteredContinents.length}
          allCountriesCount={allCountriesCount}
          activeItems={activeItems}
          onSelectAll={onSelectAll}
          onContinentChange={onContinentChange}
          onSelect={onSelect}
          selected={selected}
          activeLanguage={activeLanguage}
          translate={translate}
          prefix={prefix}
        />
      ))}
    </div>
  );
};

ContinentCountrySelect.propTypes = propTypes;
ContinentCountrySelect.defaultProps = defaultProps;
export default ContinentCountrySelect;
